import React from "react";
import { motion } from "framer-motion";
import {
  Newspaper,
  Megaphone,
  Building2,
  HeartPulse,
  ArrowRight,
  CalendarDays,
  Mail,
  Download,
  Camera,
  FileText,
  Quote,
} from "lucide-react";
import Navbar from "../../../DashBoardComponents/Navbar";

const Press = () => {
  const scrollToSection = (id) => {
    const element = document.getElementById(id);

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // =========================================================
  // PRESS / NEWS ITEMS
  // =========================================================

  const pressReleases = [
    {
      date: "Press Release",
      icon: Megaphone,
      title: "Doxzon: Building a More Connected Healthcare Experience",
      description:
        "Learn more about Doxzon's vision for connecting patients, healthcare professionals and digital healthcare services.",
    },
    {
      date: "Company Update",
      icon: Building2,
      title: "Connecting Healthcare Through Digital Experiences",
      description:
        "An overview of the ideas and technology behind Doxzon's connected healthcare ecosystem.",
    },
    {
      date: "Healthcare Technology",
      icon: HeartPulse,
      title: "The Future of Connected Digital Healthcare",
      description:
        "Exploring how digital technology can support simpler and more connected healthcare journeys.",
    },
  ];

  // =========================================================
  // MEDIA RESOURCES
  // =========================================================

  const mediaResources = [
    {
      icon: HeartPulse,
      title: "Doxzon Brand",
      description:
        "Information about Doxzon and the connected healthcare ecosystem.",
    },
    {
      icon: Camera,
      title: "Media Assets",
      description:
        "Access approved Doxzon logos and brand assets for media use.",
    },
    {
      icon: FileText,
      title: "Company Information",
      description:
        "Key information about Doxzon, its vision and healthcare platform.",
    },
  ];

  // =========================================================
  // DOXZON FACTS
  // =========================================================

  const facts = [
    "Connected healthcare ecosystem",
    "Patient-focused digital experiences",
    "Healthcare professional workflows",
    "Virtual and digital care experiences",
  ];

  return (
    <>
      <Navbar />

      <main className="bg-white text-slate-900 overflow-hidden">

        {/* =====================================================
            HERO SECTION
        ====================================================== */}

        <section className="relative overflow-hidden bg-[#f4fbfc]">

          {/* Background Glow */}

          <div className="absolute -top-40 -right-40 w-[650px] h-[650px] rounded-full bg-teal-200/25 blur-3xl" />

          <div className="absolute -bottom-40 -left-40 w-[550px] h-[550px] rounded-full bg-cyan-200/25 blur-3xl" />

          {/* Grid Background */}

          <div
            className="absolute inset-0 opacity-[0.025]"
            style={{
              backgroundImage:
                "linear-gradient(#0f172a 1px, transparent 1px), linear-gradient(90deg, #0f172a 1px, transparent 1px)",
              backgroundSize: "48px 48px",
            }}
          />

          <div className="relative max-w-7xl mx-auto px-6 pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-2 gap-16 items-center">

            {/* LEFT CONTENT */}

            <motion.div
              initial={{ opacity: 0, x: -35 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >

              <div className="inline-flex items-center gap-2 rounded-full bg-white border border-teal-100 px-4 py-2 shadow-sm">

                <span className="w-2 h-2 rounded-full bg-teal-500" />

                <span className="text-[11px] font-bold uppercase tracking-[0.2em] text-teal-700">
                  Doxzon Press
                </span>

              </div>

              <h1 className="font-sora text-5xl md:text-6xl lg:text-[62px] font-medium tracking-[-0.055em] leading-[1.03] mt-7">

                Doxzon in the

                <span className="block text-teal-500 mt-2">
                  conversation.
                </span>

              </h1>

              <p className="text-slate-600 text-lg md:text-xl leading-8 mt-7 max-w-xl">
                Find company news, announcements, media resources and
                information about Doxzon's approach to connected healthcare.
              </p>

              <div className="flex flex-wrap gap-3 mt-9">

                {/* Latest News */}

                <button
                  type="button"
                  onClick={() => scrollToSection("press-news")}
                  className="inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-6 h-12 text-sm font-semibold hover:bg-teal-500 transition"
                >
                  Latest News
                  <ArrowRight size={16} />
                </button>

                {/* Media Contact */}

                <button
                  type="button"
                  onClick={() => scrollToSection("media-contact")}
                  className="inline-flex items-center gap-2 rounded-xl bg-white border border-slate-200 text-slate-800 px-6 h-12 text-sm font-semibold hover:border-teal-200 hover:text-teal-600 transition"
                >
                  Media Contact
                  <Mail size={15} />
                </button>

              </div>

            </motion.div>


            {/* RIGHT HERO CARD */}

            <motion.div
              initial={{ opacity: 0, x: 35, scale: 0.96 }}
              animate={{ opacity: 1, x: 0, scale: 1 }}
              transition={{ duration: 0.9 }}
              className="relative"
            >

              <div className="absolute -inset-8 rounded-[45px] bg-teal-200/30 blur-3xl" />

              <div className="relative rounded-[28px] bg-white border border-teal-50 shadow-[0_24px_60px_rgba(15,23,42,0.10)] p-5">

                {/* Card Header */}

                <div className="flex items-center justify-between">

                  <div>

                    <p className="text-[10px] uppercase tracking-[0.2em] text-slate-400">
                      Doxzon Newsroom
                    </p>

                    <h3 className="font-sora text-lg font-semibold mt-1">
                      Latest from Doxzon
                    </h3>

                  </div>

                  <div className="w-10 h-10 rounded-xl bg-teal-50 grid place-items-center">

                    <Newspaper
                      size={21}
                      className="text-teal-600"
                    />

                  </div>

                </div>


                {/* Featured Story */}

                <div className="mt-5 rounded-2xl bg-slate-950 p-5">

                  <div className="w-10 h-10 rounded-xl bg-teal-500/10 grid place-items-center">

                    <Megaphone
                      size={21}
                      className="text-teal-400"
                    />

                  </div>

                  <span className="inline-block text-[10px] uppercase tracking-[0.18em] text-teal-400 font-bold mt-5">
                    Featured
                  </span>

                  <h3 className="font-sora text-xl font-semibold text-white mt-2 leading-tight">
                    Building a more connected healthcare experience
                  </h3>

                  <p className="text-slate-400 text-sm leading-6 mt-3">
                    Discover the vision behind Doxzon and its approach to
                    connected digital healthcare.
                  </p>

                  <button
                    type="button"
                    onClick={() => scrollToSection("about-doxzon")}
                    className="inline-flex items-center gap-2 text-teal-400 text-sm font-semibold mt-5 hover:text-teal-300 transition"
                  >
                    Read Story
                    <ArrowRight size={15} />
                  </button>

                </div>


                {/* QUICK LINKS */}

                <div className="grid grid-cols-2 gap-3 mt-4">

                  {[
                    {
                      icon: Newspaper,
                      title: "News",
                      action: () => scrollToSection("press-news"),
                    },
                    {
                      icon: Megaphone,
                      title: "Announcements",
                      action: () => scrollToSection("press-news"),
                    },
                    {
                      icon: Download,
                      title: "Media Assets",
                      action: () => scrollToSection("media-resources"),
                    },
                    {
                      icon: Mail,
                      title: "Media Contact",
                      action: () => scrollToSection("media-contact"),
                    },
                  ].map(({ icon: Icon, title, action }) => (

                    <button
                      type="button"
                      key={title}
                      onClick={action}
                      className="text-left rounded-xl bg-[#f0fafa] border border-teal-100 p-3 hover:bg-white hover:border-teal-200 hover:shadow-sm transition"
                    >

                      <div className="w-8 h-8 rounded-lg bg-white grid place-items-center">

                        <Icon
                          size={16}
                          className="text-teal-600"
                        />

                      </div>

                      <p className="text-xs font-semibold text-slate-800 mt-2">
                        {title}
                      </p>

                    </button>

                  ))}

                </div>

              </div>

            </motion.div>

          </div>

        </section>


        {/* =====================================================
            NEWS & ANNOUNCEMENTS
        ====================================================== */}

        <section
          id="press-news"
          className="py-20 md:py-24 scroll-mt-20"
        >

          <div className="max-w-7xl mx-auto px-6">

            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">

              <div>

                <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                  News & Announcements
                </span>

                <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                  What's happening at

                  <span className="text-teal-500">
                    {" "}Doxzon.
                  </span>

                </h2>

                <p className="text-slate-600 leading-7 mt-5 max-w-2xl">
                  Explore announcements, company updates and stories about
                  Doxzon's healthcare technology journey.
                </p>

              </div>

              <button
                type="button"
                onClick={() => scrollToSection("press-news")}
                className="inline-flex items-center gap-2 text-sm font-semibold text-teal-600 hover:text-teal-700 transition"
              >
                View All News
                <ArrowRight size={15} />
              </button>

            </div>


            {/* NEWS CARDS */}

            <div className="grid lg:grid-cols-3 gap-5 mt-10">

              {pressReleases.map((item, index) => {

                // IMPORTANT:
                // Every card gets its own meaningful icon.

                const Icon = item.icon;

                return (

                  <motion.article
                    key={item.title}
                    initial={{
                      opacity: 0,
                      y: 25,
                    }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      delay: index * 0.08,
                    }}
                    whileHover={{
                      y: -5,
                    }}
                    className="group rounded-2xl border border-teal-100 bg-[#f0fafa] overflow-hidden hover:bg-white hover:shadow-lg hover:border-teal-200 transition"
                  >

                    {/* CARD TOP */}

                    <div className="h-40 bg-gradient-to-br from-slate-950 to-teal-950 relative overflow-hidden">

                      <div className="absolute -top-16 -right-16 w-40 h-40 rounded-full bg-teal-500/20 blur-2xl" />


                      {/* MEANINGFUL ICON */}

                      <div className="absolute inset-0 flex items-center justify-center">

                        <div className="w-16 h-16 rounded-2xl bg-white/10 border border-white/10 grid place-items-center">

                          <Icon
                            size={28}
                            strokeWidth={1.8}
                            className="text-teal-300"
                          />

                        </div>

                      </div>


                      {/* CATEGORY */}

                      <span className="absolute top-4 left-4 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 px-3 py-1.5 text-[10px] uppercase tracking-wider text-teal-200 font-bold">
                        {item.date}
                      </span>

                    </div>


                    {/* CARD CONTENT */}

                    <div className="p-5">

                      <h3 className="font-sora text-lg font-semibold leading-snug group-hover:text-teal-600 transition">
                        {item.title}
                      </h3>

                      <p className="text-slate-500 text-sm leading-6 mt-3">
                        {item.description}
                      </p>


                      {/* READ MORE */}

                      <button
                        type="button"
                        onClick={() => scrollToSection("about-doxzon")}
                        className="inline-flex items-center gap-2 text-teal-600 text-sm font-semibold mt-5 hover:text-teal-700 transition"
                      >
                        Read More
                        <ArrowRight size={15} />
                      </button>


                      {/* FOOTER */}

                      <div className="flex items-center justify-between mt-5 pt-4 border-t border-teal-100">

                        <span className="inline-flex items-center gap-2 text-xs text-slate-400">

                          <CalendarDays size={13} />

                          Doxzon Press

                        </span>

                      </div>

                    </div>

                  </motion.article>

                );

              })}

            </div>

          </div>

        </section>


        {/* =====================================================
            MEDIA RESOURCES
        ====================================================== */}

        <section
          id="media-resources"
          className="py-20 md:py-24 bg-[#f4fbfc] scroll-mt-20"
        >

          <div className="max-w-7xl mx-auto px-6">

            <div className="text-center max-w-2xl mx-auto">

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                Media Resources
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Everything you need

                <span className="block text-teal-500">
                  to tell the story.
                </span>

              </h2>

              <p className="text-slate-600 leading-7 mt-5">
                Access information and approved resources for writing about
                Doxzon.
              </p>

            </div>


            {/* RESOURCE CARDS */}

            <div className="grid md:grid-cols-3 gap-5 mt-10">

              {mediaResources.map((item, index) => {

                const Icon = item.icon;

                return (

                  <motion.div
                    key={item.title}
                    initial={{
                      opacity: 0,
                      y: 20,
                    }}
                    whileInView={{
                      opacity: 1,
                      y: 0,
                    }}
                    viewport={{
                      once: true,
                    }}
                    transition={{
                      delay: index * 0.07,
                    }}
                    whileHover={{
                      y: -5,
                    }}
                    className="rounded-2xl bg-[#f0fafa] border border-teal-100 p-5 hover:bg-white hover:shadow-lg hover:border-teal-200 transition"
                  >

                    <div className="w-10 h-10 rounded-xl bg-white grid place-items-center">

                      <Icon
                        size={20}
                        className="text-teal-600"
                      />

                    </div>

                    <h3 className="font-sora text-lg font-semibold mt-4">
                      {item.title}
                    </h3>

                    <p className="text-slate-500 text-sm leading-6 mt-2">
                      {item.description}
                    </p>

                    <button
                      type="button"
                      onClick={() => scrollToSection("media-contact")}
                      className="inline-flex items-center gap-2 text-teal-600 text-sm font-semibold mt-5 hover:text-teal-700 transition"
                    >
                      Explore Resource
                      <ArrowRight size={15} />
                    </button>

                  </motion.div>

                );

              })}

            </div>

          </div>

        </section>


        {/* =====================================================
            ABOUT DOXZON
        ====================================================== */}

        <section
          id="about-doxzon"
          className="py-20 md:py-24 scroll-mt-20"
        >

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-14 items-center">

            {/* LEFT */}

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-600">
                About Doxzon
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                A simpler story:

                <span className="block text-teal-500">
                  connected healthcare.
                </span>

              </h2>

              <p className="text-slate-600 leading-8 mt-6">
                Doxzon is focused on creating connected digital healthcare
                experiences that bring patients, healthcare professionals and
                healthcare services closer together.
              </p>

              <button
                type="button"
                onClick={() => {
                  window.location.href = "/aboutdoxzon";
                }}
                className="inline-flex items-center gap-2 rounded-xl bg-slate-950 text-white px-6 h-12 text-sm font-semibold mt-7 hover:bg-teal-500 transition"
              >
                About Doxzon
                <ArrowRight size={16} />
              </button>

            </div>


            {/* RIGHT */}

            <div className="space-y-3">

              {facts.map((fact) => (

                <div
                  key={fact}
                  className="flex items-center gap-4 rounded-xl border border-teal-100 bg-[#f0fafa] p-3 hover:bg-white hover:border-teal-200 transition"
                >

                  <span className="w-9 h-9 rounded-full bg-white grid place-items-center shrink-0">

                    <HeartPulse
                      size={16}
                      className="text-teal-600"
                    />

                  </span>

                  <span className="text-sm font-medium text-slate-700">
                    {fact}
                  </span>

                </div>

              ))}

            </div>

          </div>

        </section>


        {/* =====================================================
            MEDIA CONTACT
        ====================================================== */}

        <section
          id="media-contact"
          className="py-20 bg-slate-950 text-white scroll-mt-20"
        >

          <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">

            <div>

              <span className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-400">
                Media Contact
              </span>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-4">

                Want to talk about

                <span className="block text-teal-400">
                  Doxzon?
                </span>

              </h2>

              <p className="text-slate-400 leading-7 mt-5 max-w-xl">
                For media enquiries, interview requests, company information
                or other press-related questions, contact the Doxzon team.
              </p>

            </div>


            {/* CONTACT CARD */}

            <div className="rounded-2xl bg-white/[0.05] border border-white/10 p-6">

              <div className="w-10 h-10 rounded-xl bg-teal-500/10 grid place-items-center">

                <Mail
                  size={20}
                  className="text-teal-400"
                />

              </div>

              <h3 className="font-sora text-lg font-semibold mt-5">
                Press & Media Enquiries
              </h3>

              <p className="text-slate-400 text-sm leading-6 mt-2">
                For press enquiries, reach out to the Doxzon team.
              </p>

              <a
                href="mailto:media@doxzon.com"
                className="inline-flex items-center gap-2 rounded-xl bg-teal-500 text-white px-6 h-11 text-sm font-semibold mt-5 hover:bg-teal-400 transition"
              >
                Contact Media Team
                <ArrowRight size={15} />
              </a>

            </div>

          </div>

        </section>


        {/* =====================================================
            FINAL CTA
        ====================================================== */}

        <section className="px-6 py-20">

          <div className="relative overflow-hidden max-w-7xl mx-auto rounded-[28px] bg-[#f0fafa] border border-teal-100 px-8 py-14 md:px-16 text-center">

            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-teal-200/30 blur-3xl" />

            <div className="absolute -bottom-28 -left-20 w-72 h-72 rounded-full bg-cyan-200/30 blur-3xl" />

            <div className="relative">

              <div className="mx-auto w-11 h-11 rounded-xl bg-white grid place-items-center">

                <Quote
                  size={20}
                  className="text-teal-600"
                />

              </div>

              <h2 className="font-sora text-4xl md:text-5xl font-medium mt-6">

                Healthcare is changing.

                <span className="block text-teal-500">
                  Let's shape it together.
                </span>

              </h2>

              <p className="text-slate-600 max-w-2xl mx-auto mt-5 leading-7">
                Follow Doxzon's journey as we work toward a more connected
                healthcare experience.
              </p>

              <button
                type="button"
                onClick={() => {
                  window.location.href = "/";
                }}
                className="mt-8 inline-flex items-center gap-2 rounded-full bg-slate-950 text-white px-7 py-4 font-semibold hover:bg-teal-500 transition"
              >
                Explore Doxzon
                <ArrowRight size={17} />
              </button>

            </div>

          </div>

        </section>

      </main>
    </>
  );
};

export default Press;